import React, { useState, useEffect } from 'react';
import '../css/BlogPost.css';

import { Helmet } from "react-helmet";
import withSizes from 'react-sizes';
import CodeBlock from "./CodeBlock";
import FaqSection from './FaqSection';
import BlogPosts from './BlogPosts';
import SignInButton from './buttons/SignInButton';
import PublicNavigation from './navigation/PublicNavigation';
import SectionSideNavigation from './navigation/SectionSideNavigation';
import log from '../services/log';

import fetcher from '../services/fetcher'
import Footer from './Footer';
import { blogFaq } from '../faq/faq';
import { createFaqJsonLd } from '../faq/createFaqJsonLd';
import '../css/dashboard/dashboard-theme.css';
import { isPrerendering } from '../services/prerender';
import usePrerenderReady from '../hooks/usePrerenderReady';
import GoogleOAuthBoundary from './auth/GoogleOAuthBoundary';

const ReactMarkdown = require('react-markdown');
const PUBLIC_THEME_KEY = 'public.theme';
const SOCIAL_IMAGE_URL = 'https://www.goldapi.io/images/goldapi-logo-512.png';

const headingText = children => React.Children.toArray(children).map(child => (
  typeof child === 'string' || typeof child === 'number'
    ? String(child)
    : child && child.props ? headingText(child.props.children) : ''
)).join('');

const headingSlug = value => String(value || '')
  .toLowerCase()
  .replace(/<[^>]+>/g, '')
  .replace(/[`*_~[\]()]/g, '')
  .replace(/&[a-z0-9#]+;/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'section';

const extractArticleNavigation = content => {
  const headings = [];
  const counts = {};

  String(content || '').split(/\r?\n/).forEach(line => {
    const match = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (!match) return;
    const level = match[1].length;
    const label = match[2].replace(/!?(?:\[([^\]]+)\])\([^)]*\)/g, '$1').replace(/[`*_~]/g, '').trim();
    const base = headingSlug(label);
    counts[base] = (counts[base] || 0) + 1;
    const id = counts[base] === 1 ? base : `${base}-${counts[base]}`;
    headings.push({ level, href: `#${id}`, label });
  });

  const navigationHeadings = headings[0] && headings[0].level === 1
    ? headings.slice(1)
    : headings;
  if (!navigationHeadings.length) return [];

  const sectionLevel = Math.min(...navigationHeadings.map(heading => heading.level));
  const items = [];
  let currentSection = null;

  navigationHeadings.forEach(heading => {
    if (heading.level === sectionLevel) {
      currentSection = { href: heading.href, label: heading.label, children: [] };
      items.push(currentSection);
    } else if (heading.level === sectionLevel + 1) {
      const child = { href: heading.href, label: heading.label };
      if (currentSection) currentSection.children.push(child);
      else items.push(child);
    }
  });

  return items;
};

const createHeadingRenderer = () => {
  const counts = {};
  return ({ level, children }) => {
    const base = headingSlug(headingText(children));
    counts[base] = (counts[base] || 0) + 1;
    const id = counts[base] === 1 ? base : `${base}-${counts[base]}`;
    return React.createElement(`h${level}`, { id, className: 'blog-article-heading' }, children);
  };
};

function BlogPost(props) {
  const [article, setArticle] = useState({ title: 'GoldAPI.io', subtitle: 'GoldAPI.io' });
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [dark, setDark] = useState(() => {
    if (isPrerendering()) return false;
    try { return localStorage.getItem(PUBLIC_THEME_KEY) === 'dark'; } catch (err) { return false; }
  });
  const canonicalUrl = article.canonical || `https://www.goldapi.io${props.match.url}`;
  const routeSlug = props.match && props.match.params ? props.match.params.url : null;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.subtitle,
        "mainEntityOfPage": canonicalUrl,
        "url": canonicalUrl,
        "author": {
          "@type": "Organization",
          "name": "GoldAPI.io"
        },
        "publisher": {
          "@type": "Organization",
          "name": "GoldAPI.io",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.goldapi.io/logo192.png"
          }
        }
      },
      createFaqJsonLd(blogFaq)
    ]
  };

  useEffect(() => {
    async function getBlogPost(url) {
      setIsLoading(true)
      if (props.match.url) {
        const data = await fetcher.getBlogPost(url)
        log.traceTag(data)
        if (data) {
          setArticle(data)
          setHasLoaded(true)
          setIsLoading(false)
        }
      }
    }

    getBlogPost(props.match.url)
  }, [props.match.url]);

  useEffect(() => {
    document.body.classList.toggle('dashboard-dark-mode', dark);
    return () => document.body.classList.remove('dashboard-dark-mode');
  }, [dark]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    try { localStorage.setItem(PUBLIC_THEME_KEY, next ? 'dark' : 'light'); } catch (err) { /* session-only fallback */ }
  };

  const articleNavigation = [
    { to: '/blog', label: '← Back To All Guides' },
    { href: '#article-overview', label: article.title || 'Overview' },
    ...extractArticleNavigation(article.content)
  ];
  const validateBlogPost = React.useCallback(() => {
    const jsonLdScripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
    const headlineMatches = jsonLdScripts.some(script => {
      try {
        const value = JSON.parse(script.textContent);
        const entries = value && Array.isArray(value['@graph']) ? value['@graph'] : [value];
        return entries.some(entry => entry && entry['@type'] === 'Article' && entry.headline === article.title);
      } catch (err) {
        return false;
      }
    });
    return document.title === `${article.title} | GoldAPI.io` && headlineMatches;
  }, [article.title]);
  usePrerenderReady(
    hasLoaded && !isLoading && article.slug === routeSlug && Boolean(article.content) && articleNavigation.length > 1,
    'blog-post',
    {
      selectors: [
        '#article-overview h1',
        '[data-prerender-article-body] .blog-article-heading',
        '.section-side-navigation a',
        'script[type="application/ld+json"]'
      ],
      absentSelectors: ['[data-prerender-article-body] .spinner'],
      canonical: canonicalUrl,
      jsonLdType: 'Article',
      validate: validateBlogPost
    }
  );
  const headingRenderer = createHeadingRenderer();

  return (
    <GoogleOAuthBoundary>
      <div className={`dashboard-theme blog-post-page${dark ? ' dashboard-theme--dark' : ''}`}>
        {props.renderTitle && !isLoading ?
          <Helmet>
            <meta charSet="utf-8" />
            <title>{`${article.title} | GoldAPI.io`}</title>
            <meta name="description" content={`${article.subtitle}`}></meta>
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="GoldAPI.io" />
            <meta property="og:title" content={`${article.title} | GoldAPI.io`} />
            <meta property="og:description" content={`${article.subtitle}`} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={SOCIAL_IMAGE_URL} />
            <meta property="og:image:secure_url" content={SOCIAL_IMAGE_URL} />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:width" content="512" />
            <meta property="og:image:height" content="512" />
            <meta property="og:image:alt" content="GoldAPI.io logo" />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={`${article.title} | GoldAPI.io`} />
            <meta name="twitter:description" content={`${article.subtitle}`} />
            <meta name="twitter:image" content={SOCIAL_IMAGE_URL} />
            <meta name="twitter:image:alt" content="GoldAPI.io logo" />
            <link rel="canonical" href={canonicalUrl} />
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
          </Helmet>
          : <div></div>
        }

        <PublicNavigation
          links={[
            { to: '/', label: 'Home' },
            { to: '/api-documentation', label: 'API Docs' },
            { to: '/blog', label: 'Integration Guides' },
            { to: '/blog/terms-of-service', label: 'Terms of Service' },
            { to: '/blog/privacy-policy', label: 'Privacy' },
            { href: 'mailto:support@goldapi.io', label: 'Contacts' }
          ]}
          isDarkMode={dark}
          onToggleDarkMode={toggleTheme}
        />
        <div className="blog-post-layout">
          <SectionSideNavigation items={articleNavigation} ariaLabel="Article navigation" />
          <main className="blog-post-shell" id="article-overview" data-article-slug={article.slug}>
          <div className="blog-post-hero">
            <div className="blog-post-hero-shell">
              <div className="blog-post-hero-copy text-center">
                <img
                  className="blog-post-hero-logo"
                  src="/images/goldapi-logo-512.png"
                  alt="GoldAPI.io"
                  width="64"
                  height="64"
                />
                <h1>{article.title}</h1>
                <h4 className='text-muted'>{article.subtitle}</h4>
              </div>
            </div>
          </div>

          <div className="blog-post-content-shell">
            <div className="blog-markdown-body markdown-body" data-prerender-article-body>
              {!isLoading ?
                <ReactMarkdown
                  escapeHtml={false}
                  source={article.content}
                  renderers={{ code: CodeBlock, heading: headingRenderer }}
                />
                :
                <div className="spinner my-4">
                  <div className="bounce1"></div>
                  <div className="bounce2"></div>
                  <div className="bounce3"></div>
                </div>
              }
            </div>
          </div>

          <FaqSection
            title="GoldAPI.io Developer FAQ"
            subtitle="Helpful answers for developers working with precious metals price data."
            items={blogFaq}
          />

          <section className="landing-section">
            <div className="landing-section-shell">
              <div className="landing-section-head text-center">
                <span className="lead text-muted">Integration Guides To</span>
                <h2 className="display-4 my-3">Build With GoldAPI.io Today</h2>
                <h3 className="lead text-muted my-2">Practical guides for WordPress, Shopify, Excel, Joomla, and Drupal.</h3>
              </div>
              <BlogPosts items={6} random />
              <div className="row mt-4">
                <div className="col-12 text-center">
                  <SignInButton />
                </div>
              </div>
            </div>
          </section>

          <div className="blog-post-footer">
            <Footer></Footer>
          </div>
          </main>
        </div>
      </div>
    </GoogleOAuthBoundary>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isDesktop: width >= 1024
})

BlogPost.defaultProps = {
  renderTitle: true
}

export default withSizes(mapSizesToProps)(BlogPost);
